import { formatChartScoreDisplay } from "@patient-watch/charts";
import type { EntryValueViewT, SizeT } from "@patient-watch/types";
import { cn } from "@/lib/utils";
import {
    buildHeadlineDisplayLabel,
    HEADLINE_SERIES_COLORS,
    headlineScoreNumericValue,
    partitionHeadlineScoresForDisplay,
} from "../headlineScoreDisplay";
import type { EntryCardEntryValuesVariant } from "./EntryCardEntryValues";
import { EntryValueSwitch } from "./EntryValueSwitch";

interface Props {
    entryValues: EntryValueViewT[];
    variant?: EntryCardEntryValuesVariant;
    size?: SizeT;
}

function SeriesColorDot({ color, compact }: { color: string; compact: boolean }) {
    return (
        <span
            className={cn("border-2 rounded-full aspect-square shrink-0", compact ? "w-2.5 h-2.5" : "w-3 h-3")}
            style={{ backgroundColor: `${color} / 0.5`, borderColor: color }}
        />
    );
}

function scoreFraction(ev: EntryValueViewT, value: number): string {
    return formatChartScoreDisplay(value, ev.form_id ?? "", ev.max_score);
}

interface ScoreRowProps {
    label: string;
    value: number | null;
    fraction: string | null;
    compact: boolean;
    highlighted?: boolean;
    color?: string;
}

function ScoreRow({ label, value, fraction, compact, highlighted = false, color }: ScoreRowProps) {
    return (
        <div className={cn("flex justify-between items-center gap-3 w-full", highlighted && !compact && "py-0.5")}>
            <span
                className={cn(
                    "flex items-center gap-2 min-w-0",
                    highlighted
                        ? compact
                            ? "font-semibold text-slate-700 dark:text-slate-200 text-sm uppercase tracking-wide"
                            : "font-bold text-slate-700 dark:text-slate-200 text-sm uppercase tracking-wider"
                        : compact
                          ? "text-slate-500 dark:text-slate-400 text-sm"
                          : "text-slate-500 dark:text-slate-400 text-xs"
                )}
            >
                {color ? <SeriesColorDot color={color} compact={compact} /> : null}
                <span className="truncate">{label}</span>
            </span>
            <span
                className={cn(
                    "tabular-nums shrink-0",
                    highlighted
                        ? compact
                            ? "font-semibold text-indigo-600 dark:text-indigo-400 text-base"
                            : "font-semibold text-indigo-600 dark:text-indigo-400 text-sm"
                        : compact
                          ? "font-medium text-slate-700 dark:text-slate-300 text-sm"
                          : "font-medium text-slate-700 dark:text-slate-300 text-xs"
                )}
            >
                {value !== null && fraction ? fraction : "—"}
            </span>
        </div>
    );
}

function renderSeriesBlock(
    series: EntryValueViewT[],
    displayLabel: (label: string) => string,
    compact: boolean,
    withDivider: boolean
) {
    if (series.length === 0) return null;

    return (
        <div
            className={cn(
                "flex flex-col",
                withDivider
                    ? compact
                        ? "gap-2 pt-2 border-slate-200 dark:border-slate-700 border-t"
                        : "gap-2 pt-2 border-slate-200 border-t"
                    : compact
                      ? "gap-2"
                      : "gap-2"
            )}
        >
            {series.map((ev, index) => {
                const numeric = headlineScoreNumericValue(ev);
                return (
                    <ScoreRow
                        key={ev.entry_value_id}
                        label={displayLabel(ev.input_label)}
                        value={numeric}
                        fraction={numeric !== null ? scoreFraction(ev, numeric) : null}
                        compact={compact}
                        color={HEADLINE_SERIES_COLORS[index % HEADLINE_SERIES_COLORS.length]}
                    />
                );
            })}
        </div>
    );
}

const HeadlineEntryValues = ({ entryValues, variant = "default", size }: Props) => {
    const compact = variant === "plain";
    const { primary, series, other } = partitionHeadlineScoresForDisplay(entryValues);
    const allLabels = [
        ...(primary ? [primary.input_label] : []),
        ...series.map((ev) => ev.input_label),
        ...other.map((ev) => ev.input_label),
    ];
    const displayLabel = buildHeadlineDisplayLabel(allLabels);
    const hasChartMetadata = primary !== null || series.length > 0;

    if (!hasChartMetadata) {
        return (
            <>
                {entryValues.map((ev) => (
                    <div
                        key={ev.entry_value_id}
                        className={cn(
                            "flex flex-row gap-4 w-full",
                            compact
                                ? "text-sm"
                                : "px-2 py-0 border border-slate-200 rounded-md font-bold text-slate-900",
                            !compact && (size === "sm" ? "text-md" : "text-lg")
                        )}
                    >
                        <EntryValueSwitch size={compact ? "sm" : "lg"} entryValue={ev} />
                    </div>
                ))}
            </>
        );
    }

    const primaryNumeric = primary ? headlineScoreNumericValue(primary) : null;

    return (
        <div className="flex flex-col gap-2 w-full" data-testid="headline-scores">
            {primary ? (
                <ScoreRow
                    label={displayLabel(primary.input_label)}
                    value={primaryNumeric}
                    fraction={primaryNumeric !== null ? scoreFraction(primary, primaryNumeric) : null}
                    compact={compact}
                    highlighted
                />
            ) : null}
            {renderSeriesBlock(series, displayLabel, compact, primary !== null)}
            {other.map((ev) => (
                <div key={ev.entry_value_id} className="flex flex-row gap-4 w-full">
                    <EntryValueSwitch size={compact ? "sm" : "lg"} entryValue={ev} />
                </div>
            ))}
        </div>
    );
};

export default HeadlineEntryValues;
